import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toastOk, toastErro, msgErro, confirmar } from '../../services/ui';
import { useSort } from '../../helpers/useSort';

export default function Fornecedores() {
  const navigate = useNavigate();
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  // busca no ERP
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState(null);
  const debounce = useRef();
  const s = useSort('nome');

  const carregar = async () => {
    try {
      const { data } = await api.get('/fornecedores');
      setLista(data);
    } catch (e) {
      setErro(msgErro(e, 'Erro ao carregar fornecedores.'));
    } finally {
      setCarregando(false);
    }
  };
  useEffect(() => { carregar(); }, []);

  useEffect(() => {
    clearTimeout(debounce.current);
    if (busca.trim().length < 2) { setResultados([]); return; }
    setBuscando(true);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/fornecedores/buscar-erp', { params: { q: busca.trim() } });
        setResultados(data);
      } catch { setResultados([]); }
      finally { setBuscando(false); }
    }, 350);
    return () => clearTimeout(debounce.current);
  }, [busca]);

  const cadastrar = async (r) => {
    setSalvando(r.fornecedor_oid);
    try {
      await api.post('/fornecedores', { fornecedor_oid: r.fornecedor_oid });
      setBusca(''); setResultados([]);
      await carregar();
      toastOk(`${r.nome} cadastrado.`);
    } catch (e) {
      toastErro(msgErro(e, 'Erro ao cadastrar.'));
    } finally { setSalvando(null); }
  };

  const toggleAtivo = async (f, ev) => {
    ev.stopPropagation();
    if (f.ativo) {
      const ok = await confirmar({
        titulo: `Desativar ${f.nome}?`,
        texto: 'Os usuários desse fornecedor perdem o acesso ao portal.',
        confirmarTexto: 'Desativar', perigo: true,
      });
      if (!ok) return;
    }
    try {
      await api.patch(`/fornecedores/${f.id}`, { ativo: !f.ativo });
      setLista((l) => l.map((x) => (x.id === f.id ? { ...x, ativo: !x.ativo } : x)));
      toastOk(f.ativo ? 'Fornecedor desativado.' : 'Fornecedor ativado.');
    } catch (e) { toastErro(msgErro(e)); }
  };

  const jaCadastrado = (oid) => lista.some((f) => f.fornecedor_oid === oid);

  return (
    <div>
      <h4 className="fw-bold mb-1">
        <i className="bi bi-building me-2" style={{ color: 'var(--primary)' }} />
        Fornecedores
      </h4>
      <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
        Cadastre parceiros a partir do ERP e gerencie o acesso deles.
        {' · '}
        <button className="btn-link" style={{ fontSize: '0.85rem' }} onClick={() => navigate('/admin/ajuda')}>
          <i className="bi bi-question-circle me-1" />Como funciona
        </button>
      </p>

      {/* Buscar no ERP */}
      <div className="card p-3 mb-4">
        <label className="fw-semibold mb-2">
          <i className="bi bi-search me-1" /> Buscar fornecedor no ERP <span className="text-muted fw-normal">(nome, CNPJ ou código)</span>
        </label>
        <input
          className="form-control"
          placeholder="Ex.: Tambasa, 17.359.233/0001-88..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        {buscando && <div className="text-muted mt-2" style={{ fontSize: '0.85rem' }}><span className="spinner-border spinner-border-sm me-1" role="status" style={{ verticalAlign: '-2px' }} />buscando no ERP...</div>}
        {resultados.length > 0 && (
          <div className="mt-3" style={{ border: '1px solid var(--borda)', borderRadius: 10, overflow: 'hidden' }}>
            {resultados.map((r) => (
              <div key={r.fornecedor_oid} className="d-flex align-items-center justify-content-between p-2 px-3"
                style={{ borderBottom: '1px solid var(--borda)' }}>
                <div>
                  <div className="fw-semibold">{r.nome}</div>
                  <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                    {r.cnpj || 'sem CNPJ'} · cód ERP {r.codfor || '—'}
                  </div>
                </div>
                {jaCadastrado(r.fornecedor_oid) ? (
                  <span className="badge-neutro">já cadastrado</span>
                ) : (
                  <button className="btn-primary-for" disabled={salvando === r.fornecedor_oid}
                    onClick={() => cadastrar(r)}>
                    {salvando === r.fornecedor_oid ? '...' : 'Cadastrar'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lista cadastrada */}
      {carregando ? (
        <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} role="status" /></div>
      ) : erro ? (
        <div className="text-center text-danger py-4"><i className="bi bi-exclamation-triangle me-1" />{erro}</div>
      ) : lista.length === 0 ? (
        <div className="text-center text-muted py-4">Nenhum fornecedor cadastrado ainda. Busque acima para começar.</div>
      ) : (
        <div className="card p-0">
          <div className="table-responsive">
            <table className="table table-hover table-for mb-0 align-middle">
              <thead>
                <tr>
                  <th onClick={() => s.toggle('nome')}>Fornecedor {s.icon('nome')}</th>
                  <th onClick={() => s.toggle('cnpj')}>CNPJ {s.icon('cnpj')}</th>
                  <th onClick={() => s.toggle('codfor')}>Cód. ERP {s.icon('codfor')}</th>
                  <th className="text-center" onClick={() => s.toggle('qtd_usuarios')}>Usuários {s.icon('qtd_usuarios')}</th>
                  <th className="text-center" onClick={() => s.toggle('ativo')}>Status {s.icon('ativo')}</th>
                  <th className="nao-ord"><span className="visually-hidden">Ações</span></th>
                </tr>
              </thead>
              <tbody>
                {s.aplicar(lista, (r, k) => (k === 'ativo' ? (r.ativo ? 1 : 0) : r[k])).map((f) => (
                  <tr key={f.id} className="linha-clicavel" onClick={() => navigate(`/admin/usuarios/${f.id}`)}>
                    <td>
                      <div className="fw-semibold">{f.nome}</div>
                      {f.razao_social && f.razao_social !== f.nome && (
                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>{f.razao_social}</div>
                      )}
                    </td>
                    <td>{f.cnpj || '—'}</td>
                    <td>{f.codfor || '—'}</td>
                    <td className="text-center"><span className="badge-filial">{f.qtd_usuarios}</span></td>
                    <td className="text-center">
                      <span className={f.ativo ? 'badge-positivo' : 'badge-negativo'}>{f.ativo ? 'Ativo' : 'Inativo'}</span>
                    </td>
                    <td className="text-end text-nowrap">
                      <button className="btn btn-sm btn-link p-0 me-3" onClick={(e) => { e.stopPropagation(); navigate(`/admin/usuarios/${f.id}`); }}>
                        <i className="bi bi-people me-1" />Usuários
                      </button>
                      <button className="btn btn-sm btn-link p-0" onClick={(e) => toggleAtivo(f, e)}>
                        {f.ativo ? 'Desativar' : 'Ativar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
